/**
 * skyship_crew.ts — the sky-ship's crew: deterministic roster, stations,
 * names, and the topic dialogue (pure content — no THREE, no DOM).
 *
 * Every city's spaceport ship now rides HIGH over the skyline (the sky-ship
 * wave); aboard it lives a full working crew, client-rendered and talkable
 * through the topic window. Everything derives from (seed, site): same ship,
 * same crew, same words, on every client.
 *
 * Dialogue design: Morrowind-style topics (the CLIENT-ONLY topic window
 * law). Every crew member answers the shared ship topics in their own
 * role's voice where it matters, and the "city below" topics use the REAL
 * derived city name + civilization voice.
 */
import type { SettlementSite } from '../../../shared/settlements.js';
import { settlementSites } from '../../../shared/settlements.js';
import { civilizationOf, type Civilization } from './biome_settlements.js';
import { settlementNamesFor } from './settlement_names.js';

/** FNV-1a → [0,1) (the settlement hash family). */
function hs01(seed: number, salt: string): number {
  let h = 0x811c9dc5;
  const s = `${seed}|${salt}`;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return (h >>> 0) / 0xffffffff;
}

// ── The ship's name ─────────────────────────────────────────────────────────

const A_PREFIX = ['Sovereign', 'Radiance', 'Gilded Oath', 'Dawnward', 'Crown', 'Meridian', 'Lumen', 'Auric Wing'];
const B_PREFIX = ['Warden', 'Frostward', 'Deepkeel', 'Northlight', 'Icebound Oath', 'Stormhold', 'Bulwark', 'Winterwing'];

const nameCache = new Map<string, Map<number, string>>();
function cityName(seed: string, site: SettlementSite): string {
  let m = nameCache.get(seed);
  if (!m) {
    m = settlementNamesFor(settlementSites(seed), (ss) => civilizationOf(ss));
    nameCache.set(seed, m);
  }
  return m.get(site.id) ?? 'the city';
}

/** "Sovereign of Ostorch" / "Warden of Fenicrest" — the ship over each city. */
export function skyShipName(seed: string, site: SettlementSite): string {
  const civ = civilizationOf(site.siteSeed);
  const pool = civ === 'a' ? A_PREFIX : B_PREFIX;
  const p = pool[Math.floor(hs01(site.siteSeed, 'shipname') * pool.length) % pool.length];
  return `${p} of ${cityName(seed, site)}`;
}

// ── Crew names (syllable style follows the settlement-name voices) ──────────

const A_GIVEN = ['Auren', 'Caelis', 'Miro', 'Solenne', 'Tavia', 'Oris', 'Veyra', 'Lucan', 'Seraphe', 'Corin', 'Alva', 'Nerio', 'Thessa', 'Marcel', 'Ovid', 'Priya'];
const B_GIVEN = ['Ulla', 'Bjorn', 'Eira', 'Halvar', 'Sigrun', 'Kald', 'Ylva', 'Torben', 'Ingrid', 'Stellan', 'Freda', 'Njall', 'Runa', 'Vidar', 'Maren', 'Oskar'];
const A_SUR = ['Solvane', 'Corvale', 'Aurelian', 'Vessendry', 'Thalric', 'Ostmere', 'Calessa', 'Virelli'];
const B_SUR = ['Skjolsen', 'Fenwick', 'Brynjar', 'Isgard', 'Kaldbrook', 'Ulfsdottir', 'Stenmark', 'Havardsen'];

function crewName(siteSeed: number, civ: Civilization, idx: number): string {
  const G = civ === 'a' ? A_GIVEN : B_GIVEN;
  const S = civ === 'a' ? A_SUR : B_SUR;
  const g = G[Math.floor(hs01(siteSeed, `cg${idx}`) * G.length) % G.length];
  const s = S[Math.floor(hs01(siteSeed, `cs${idx}`) * S.length) % S.length];
  return `${g} ${s}`;
}

// ── Roster ──────────────────────────────────────────────────────────────────

export type CrewRole =
  | 'captain' | 'first_officer' | 'helmsman' | 'comms' | 'sensors'
  | 'chief_engineer' | 'engineer' | 'quartermaster' | 'cargohand'
  | 'cook' | 'medic' | 'deckhand' | 'steward';

export interface CrewMember {
  idx: number;
  name: string;
  role: CrewRole;
  roleTitle: string;
  /** Ship-local station (x = starboard+, y = height above ship origin, z = bow+). */
  x: number; y: number; z: number;
  /** Facing yaw in ship-local frame. */
  yaw: number;
  /** Deck label for the roster/diagnostics. */
  deck: string;
  /** Duty route (ship-local, same deck); the module walks it. */
  route: CrewWaypoint[];
  /** The generated figure this hand wears. */
  archetype: CrewArchetype;
}

/** Deck heights (ship-local Y of each FLOOR) — mirrored by the interior
 *  builder; keep in lockstep with lib/skyship_interior.ts. */
export const DECK_Y = { engineering: 6, crew: 10, officers: 14, bridge: 18 } as const;

/** A duty waypoint: a ship-local XZ on the member's own deck, the yaw to
 *  face while working there, and how long to stay (seconds). */
export interface CrewWaypoint { x: number; z: number; yaw: number; dwell: number }

/**
 * THE CREW ARE BUSY (field report 2026-09-03: "they are too static — have
 * them look like they are busy on the ship"). Every hand has a duty ROUTE:
 * two to four waypoints on their deck (a console, a crate stack, a bunk, a
 * rail) that they walk between at a working pace, dwelling at each — facing
 * the work, turning to check the next thing, stepping aside. Routes stay
 * inside the rooms' legal rects (clampShipLocal) and clear of the bulkheads
 * and partitions; the module animates the walk (bob, lean, turn) since the
 * figures are unrigged. Pure content: the harness pins every waypoint legal.
 */
const ROSTER: Array<{ role: CrewRole; roleTitle: string; x: number; y: number; z: number; yaw: number; deck: string; route: CrewWaypoint[] }> = [
  // ── Bridge (D1, y18, z 6..30, hw 8) — the command arc faces the bow viewport. ──
  { role: 'captain', roleTitle: 'Captain', x: 0, y: DECK_Y.bridge, z: 16, yaw: 0, deck: 'bridge',
    route: [{ x: 0, z: 16, yaw: 0, dwell: 14 }, { x: 0, z: 11.5, yaw: Math.PI, dwell: 9 }, { x: -3.2, z: 14, yaw: -0.6, dwell: 7 }] },
  { role: 'first_officer', roleTitle: 'First Officer', x: -2.4, y: DECK_Y.bridge, z: 17.5, yaw: 0, deck: 'bridge',
    route: [{ x: -2.4, z: 17.5, yaw: 0, dwell: 10 }, { x: 3.2, z: 12, yaw: -2.4, dwell: 8 }, { x: -5.8, z: 15, yaw: -1.6, dwell: 6 }] },
  { role: 'helmsman', roleTitle: 'Helmsman', x: 0, y: DECK_Y.bridge, z: 21.5, yaw: 0, deck: 'bridge',
    route: [{ x: 0, z: 21.5, yaw: 0, dwell: 30 }, { x: 1.6, z: 20.5, yaw: 0.4, dwell: 5 }] },
  { role: 'comms', roleTitle: 'Comms Officer', x: -4.6, y: DECK_Y.bridge, z: 20, yaw: 0.5, deck: 'bridge',
    route: [{ x: -4.6, z: 20, yaw: 0.5, dwell: 16 }, { x: -6.2, z: 14, yaw: 1.6, dwell: 6 }] },
  { role: 'sensors', roleTitle: 'Sensors Officer', x: 4.6, y: DECK_Y.bridge, z: 20, yaw: -0.5, deck: 'bridge',
    route: [{ x: 4.6, z: 20, yaw: -0.5, dwell: 18 }, { x: 5.4, z: 11, yaw: -0.7, dwell: 7 }] },
  { role: 'first_officer', roleTitle: 'Watch Officer', x: 2.6, y: DECK_Y.bridge, z: 8.5, yaw: 0, deck: 'bridge',
    route: [{ x: 2.6, z: 8.5, yaw: 0, dwell: 9 }, { x: -1.8, z: 9, yaw: 0.3, dwell: 6 }, { x: 2.2, z: 24, yaw: 0, dwell: 8 }] },
  // ── Officers' deck (D2, y14, z −30..14, hw 10): cabins port, medbay starboard. ──
  { role: 'medic', roleTitle: "Ship's Medic", x: 6.5, y: DECK_Y.officers, z: -6, yaw: Math.PI / 2, deck: 'officers',
    route: [{ x: 5.2, z: -6, yaw: Math.PI / 2, dwell: 10 }, { x: 5.2, z: -11, yaw: Math.PI / 2, dwell: 8 }, { x: 7.2, z: -15, yaw: 1.2, dwell: 6 }] },
  { role: 'medic', roleTitle: 'Nurse', x: 5.5, y: DECK_Y.officers, z: -9, yaw: 1.2, deck: 'officers',
    route: [{ x: 5.4, z: -8.5, yaw: 1.2, dwell: 7 }, { x: 4.4, z: -3.2, yaw: 0.2, dwell: 6 }, { x: 4.6, z: -14.5, yaw: 2.2, dwell: 9 }] },
  { role: 'steward', roleTitle: 'Cabin Steward', x: -5.5, y: DECK_Y.officers, z: 0, yaw: -Math.PI / 2, deck: 'officers',
    route: [{ x: -5.6, z: 6, yaw: -Math.PI / 2, dwell: 8 }, { x: -5.6, z: -2, yaw: -Math.PI / 2, dwell: 8 }, { x: -5.6, z: -10, yaw: -Math.PI / 2, dwell: 8 }, { x: -2.4, z: -14, yaw: Math.PI, dwell: 5 }] },
  { role: 'first_officer', roleTitle: 'Purser', x: -5, y: DECK_Y.officers, z: 10, yaw: -1.2, deck: 'officers',
    route: [{ x: -4.8, z: 9.6, yaw: -1.2, dwell: 14 }, { x: -1.2, z: 12, yaw: 0.4, dwell: 6 }] },
  // ── Crew deck (D3, y10, z −40..30, hw 13): mess port-mid, galley aft, berthing starboard, lounge forward. ──
  { role: 'cook', roleTitle: "Ship's Cook", x: -8.5, y: DECK_Y.crew, z: 6, yaw: Math.PI / 2, deck: 'crew',
    route: [{ x: -8.4, z: 6, yaw: -Math.PI / 2, dwell: 12 }, { x: -9.4, z: -2, yaw: -Math.PI / 2, dwell: 6 }, { x: -4.6, z: 5, yaw: -Math.PI / 2, dwell: 5 }] },
  { role: 'cook', roleTitle: 'Galley Hand', x: -7, y: DECK_Y.crew, z: 9.5, yaw: 0, deck: 'crew',
    route: [{ x: -7.2, z: 9.8, yaw: 0, dwell: 7 }, { x: -4.6, z: 2, yaw: -Math.PI / 2, dwell: 5 }, { x: -9.6, z: 1, yaw: -2.2, dwell: 6 }] },
  { role: 'steward', roleTitle: 'Steward', x: -6, y: DECK_Y.crew, z: 24, yaw: Math.PI, deck: 'crew',
    route: [{ x: -6, z: 24, yaw: Math.PI, dwell: 9 }, { x: 0, z: 26.5, yaw: Math.PI, dwell: 6 }, { x: 6, z: 24, yaw: Math.PI, dwell: 9 }] },
  { role: 'steward', roleTitle: 'Lounge Steward', x: 3, y: DECK_Y.crew, z: 20, yaw: 0, deck: 'crew',
    route: [{ x: 3, z: 20, yaw: 0.6, dwell: 8 }, { x: -3, z: 19, yaw: -0.6, dwell: 8 }, { x: 0, z: 27, yaw: 0, dwell: 5 }] },
  { role: 'deckhand', roleTitle: 'Deckhand', x: 7.5, y: DECK_Y.crew, z: -10, yaw: -Math.PI / 2, deck: 'crew',
    route: [{ x: 6.4, z: -4, yaw: Math.PI / 2, dwell: 6 }, { x: 6.4, z: -9, yaw: Math.PI / 2, dwell: 6 }, { x: 6.4, z: -14, yaw: Math.PI / 2, dwell: 6 }, { x: 8.4, z: -20.5, yaw: Math.PI, dwell: 8 }] },
  { role: 'deckhand', roleTitle: 'Boatswain', x: 0, y: DECK_Y.crew, z: -30, yaw: Math.PI, deck: 'crew',
    route: [{ x: 0, z: -30, yaw: Math.PI, dwell: 8 }, { x: -6, z: -24, yaw: -1.2, dwell: 6 }, { x: 0, z: -12, yaw: 0, dwell: 7 }] },
  { role: 'engineer', roleTitle: 'Apprentice Engineer', x: -8, y: DECK_Y.crew, z: -26, yaw: 2.4, deck: 'crew',
    route: [{ x: -8, z: -26, yaw: 2.4, dwell: 9 }, { x: -3, z: -34, yaw: Math.PI, dwell: 7 }, { x: -9.5, z: -32, yaw: -2.6, dwell: 5 }] },
  // ── Engineering + cargo (D4, y6, z −40..26, hw 13): the reactor aft of the z −16 bulkhead, cargo forward. ──
  { role: 'chief_engineer', roleTitle: 'Chief Engineer', x: 0, y: DECK_Y.engineering, z: -26, yaw: Math.PI, deck: 'engineering',
    route: [{ x: 0, z: -25, yaw: Math.PI, dwell: 12 }, { x: -4.6, z: -21, yaw: 2.4, dwell: 8 }, { x: 4.6, z: -21, yaw: -2.4, dwell: 8 }] },
  { role: 'engineer', roleTitle: 'Engineer', x: -5.5, y: DECK_Y.engineering, z: -20, yaw: 2.2, deck: 'engineering',
    route: [{ x: -5.6, z: -20.5, yaw: 2.2, dwell: 10 }, { x: -10.4, z: -30, yaw: -Math.PI / 2, dwell: 8 }, { x: -7.6, z: -35, yaw: Math.PI, dwell: 5 }] },
  { role: 'engineer', roleTitle: 'Engineer', x: 5.5, y: DECK_Y.engineering, z: -30, yaw: -2.2, deck: 'engineering',
    route: [{ x: 5.6, z: -30, yaw: -2.2, dwell: 9 }, { x: 8.2, z: -34, yaw: 0.8, dwell: 7 }, { x: 10.2, z: -26, yaw: Math.PI / 2, dwell: 8 }] },
  { role: 'engineer', roleTitle: 'Coolant Tech', x: 9, y: DECK_Y.engineering, z: -36.5, yaw: 0.6, deck: 'engineering',
    route: [{ x: 8.6, z: -36.6, yaw: 0.6, dwell: 12 }, { x: 4.2, z: -38, yaw: Math.PI, dwell: 6 }] },
  { role: 'cargohand', roleTitle: 'Hold Hand', x: -3, y: DECK_Y.engineering, z: 10, yaw: -1.2, deck: 'cargo',
    route: [{ x: -3.4, z: 9, yaw: -1.6, dwell: 7 }, { x: 1.2, z: 14, yaw: 0.5, dwell: 6 }, { x: -2.6, z: 20, yaw: Math.PI, dwell: 6 }, { x: -7, z: -13.4, yaw: -Math.PI / 2, dwell: 8 }] },
  { role: 'cargohand', roleTitle: 'Loader', x: 5, y: DECK_Y.engineering, z: 4, yaw: 0.6, deck: 'cargo',
    route: [{ x: 5, z: 4, yaw: 0.6, dwell: 6 }, { x: 4.6, z: 12, yaw: 1.2, dwell: 8 }, { x: 0.5, z: 22, yaw: -0.4, dwell: 7 }] },
  // ── The concourse (y10, z −300..−40, hw 45): the hangar hall — cargo, lifeboats, the airlock. ──
  { role: 'quartermaster', roleTitle: 'Quartermaster', x: 4, y: DECK_Y.crew, z: -280, yaw: Math.PI, deck: 'concourse',
    route: [{ x: 4, z: -280, yaw: Math.PI, dwell: 12 }, { x: -2, z: -290, yaw: Math.PI, dwell: 8 }, { x: -24, z: -244, yaw: -1.2, dwell: 8 }] },
  { role: 'cargohand', roleTitle: 'Cargohand', x: -22, y: DECK_Y.crew, z: -236, yaw: 1.2, deck: 'concourse',
    route: [{ x: -22, z: -236, yaw: 1.2, dwell: 8 }, { x: -12, z: -218, yaw: 2.0, dwell: 7 }, { x: -25, z: -184, yaw: -1.4, dwell: 7 }] },
  { role: 'cargohand', roleTitle: 'Cargohand', x: 24, y: DECK_Y.crew, z: -206, yaw: -1.7, deck: 'concourse',
    route: [{ x: 24, z: -206, yaw: -1.7, dwell: 8 }, { x: 16, z: -184, yaw: 0.9, dwell: 7 }, { x: 26, z: -226, yaw: 1.4, dwell: 7 }] },
  { role: 'cargohand', roleTitle: 'Cargomaster', x: 8, y: DECK_Y.crew, z: -215, yaw: Math.PI, deck: 'concourse',
    route: [{ x: 8, z: -215, yaw: Math.PI, dwell: 9 }, { x: 0, z: -160, yaw: 0, dwell: 6 }, { x: 16, z: -110, yaw: -0.5, dwell: 8 }] },
  { role: 'deckhand', roleTitle: 'Lifeboat Hand', x: -32, y: DECK_Y.crew, z: -270, yaw: Math.PI / 2, deck: 'concourse',
    route: [{ x: -32, z: -270, yaw: -Math.PI / 2, dwell: 12 }, { x: -30, z: -258, yaw: -0.8, dwell: 5 }, { x: 32, z: -268, yaw: Math.PI / 2, dwell: 12 }] },
  { role: 'engineer', roleTitle: 'Hall Engineer', x: 30, y: DECK_Y.crew, z: -84, yaw: -Math.PI / 2, deck: 'concourse',
    route: [{ x: 28, z: -82, yaw: Math.PI / 2, dwell: 10 }, { x: 34, z: -116, yaw: 0, dwell: 8 }, { x: 40, z: -158, yaw: Math.PI / 2, dwell: 6 }] },
  { role: 'comms', roleTitle: 'Signal Officer', x: 6, y: DECK_Y.crew, z: -288, yaw: 0, deck: 'concourse',
    route: [{ x: 6, z: -287, yaw: 0, dwell: 14 }, { x: 10, z: -280, yaw: 1.2, dwell: 5 }] },
  { role: 'steward', roleTitle: 'Hall Steward', x: -10, y: DECK_Y.crew, z: -48, yaw: 0, deck: 'concourse',
    route: [{ x: -10, z: -48, yaw: 0.4, dwell: 8 }, { x: 10, z: -48, yaw: -0.4, dwell: 8 }, { x: 0, z: -70, yaw: Math.PI, dwell: 6 }] },
  // ── The spine gallery (y10, z 30..385, hw 7): the windowed walk. ──
  { role: 'deckhand', roleTitle: 'Gallery Watch', x: 0, y: DECK_Y.crew, z: 120, yaw: 0, deck: 'gallery',
    route: [{ x: -3.8, z: 70, yaw: -Math.PI / 2, dwell: 8 }, { x: 3.8, z: 140, yaw: Math.PI / 2, dwell: 8 }, { x: -3.8, z: 245, yaw: -Math.PI / 2, dwell: 8 }] },
  { role: 'steward', roleTitle: 'Promenade Steward', x: 0, y: DECK_Y.crew, z: 300, yaw: 0, deck: 'gallery',
    route: [{ x: 3.6, z: 320, yaw: Math.PI / 2, dwell: 9 }, { x: 3.8, z: 350, yaw: Math.PI / 2, dwell: 7 }, { x: -2, z: 280, yaw: Math.PI, dwell: 6 }] },
  // ── The prow watch (the huge bow deck, y10, z 385..645). ──
  { role: 'deckhand', roleTitle: 'Deckhand', x: -10, y: DECK_Y.crew, z: 440, yaw: 0.2, deck: 'foredeck',
    route: [{ x: -10, z: 440, yaw: 0.2, dwell: 8 }, { x: -13, z: 418, yaw: -1.0, dwell: 8 }, { x: -4, z: 470, yaw: 0.6, dwell: 6 }] },
  { role: 'deckhand', roleTitle: 'Foredeck Watch', x: 4, y: DECK_Y.crew, z: 620, yaw: 0, deck: 'foredeck',
    route: [{ x: 4, z: 620, yaw: 0, dwell: 16 }, { x: -4, z: 636, yaw: 0, dwell: 10 }, { x: 5, z: 606, yaw: -1.2, dwell: 5 }] },
  { role: 'deckhand', roleTitle: 'Lookout', x: 0, y: DECK_Y.crew, z: 555, yaw: 0, deck: 'foredeck',
    route: [{ x: -2, z: 556, yaw: 0, dwell: 12 }, { x: -14, z: 520, yaw: -1.4, dwell: 8 }, { x: 14, z: 520, yaw: 1.4, dwell: 8 }] },
  { role: 'sensors', roleTitle: 'Navigator', x: 2.5, y: DECK_Y.crew, z: 562, yaw: Math.PI, deck: 'foredeck',
    route: [{ x: 2.2, z: 562.5, yaw: Math.PI, dwell: 14 }, { x: 8, z: 458, yaw: 0.3, dwell: 7 }] },
];

/** Which generated figure (`avatar:crew_<archetype>`, gen-skyship-crew.mjs) stands in for a role. */
export type CrewArchetype = 'captain' | 'officer' | 'engineer' | 'medic' | 'deckhand' | 'cook' | 'quartermaster' | 'steward';
const ARCHETYPE_OF: Record<CrewRole, CrewArchetype> = {
  captain: 'captain', first_officer: 'officer', helmsman: 'officer', comms: 'officer', sensors: 'officer',
  chief_engineer: 'engineer', engineer: 'engineer', quartermaster: 'quartermaster', cargohand: 'quartermaster',
  cook: 'cook', medic: 'medic', deckhand: 'deckhand', steward: 'steward',
};
export function crewArchetype(role: CrewRole): CrewArchetype { return ARCHETYPE_OF[role]; }

/**
 * What a hand is DOING for the animation grammar (2026-09-07: the crew wear the
 * Meshy rigs aboard, so a duty phase maps to a versona state → `clipFor`):
 * walking a route = moving; dwelling = the archetype's work — the captain and
 * officer keep watch / confer, the steward attends, everyone else works.
 */
export function crewDuty(archetype: CrewArchetype, phase: 'dwell' | 'walk'): { state: string; talking: boolean } {
  if (phase === 'walk') return { state: 'working', talking: false };
  switch (archetype) {
    case 'captain': return { state: 'patrolling', talking: false };
    case 'officer': return { state: 'chatting', talking: true };
    case 'steward': return { state: 'chatting', talking: false };
    default: return { state: 'working', talking: false };
  }
}

/** The full deterministic crew for one city's sky-ship. */
export function skyShipCrew(seed: string, site: SettlementSite): CrewMember[] {
  const civ = civilizationOf(site.siteSeed);
  const used = new Set<string>();
  return ROSTER.map((r, idx) => {
    // Distinct names: a 16×8 syllable space collides — re-roll on collision
    // with a shifted salt (deterministic, same on every client).
    let name = crewName(site.siteSeed, civ, idx);
    for (let roll = 1; used.has(name) && roll < 24; roll++) name = crewName(site.siteSeed, civ, idx + roll * 100);
    used.add(name);
    return {
      idx, name,
      role: r.role, roleTitle: r.roleTitle,
      x: r.x, y: r.y, z: r.z, yaw: r.yaw, deck: r.deck,
      route: r.route, archetype: ARCHETYPE_OF[r.role],
    };
  });
}

// ── Topic dialogue ──────────────────────────────────────────────────────────

export interface CrewTopic { key: string; label: string; text: string }

/** Role flavor for "your work". */
const WORK_LINES: Record<CrewRole, [string, string]> = {
  captain: [
    'I hold her steady over {CITY} and answer for every soul aboard. The Concord gave me the conn; the sky gave me the rest.',
    'The Compact trusts me with her keel and her crew. We hold station over {CITY} in every weather the sky sends.',
  ],
  first_officer: [
    'I keep the watch bill, the manifests, and the captain\'s patience. Nothing moves on {SHIP} without my initial.',
    'Rosters, rations, discipline. The captain commands; I make the command happen.',
  ],
  helmsman: [
    'Station-keeping sounds dull until a squall line rolls off the ridge. Then you learn what a helm is for.',
    'She drifts a hand\'s width in an hour, and I put that hand\'s width back. That is the whole art.',
  ],
  comms: [
    'Every word between {SHIP} and the ground passes my board — port traffic, freight calls, the crier\'s gossip if I\'m honest.',
    'I listen to the whole sky. Most of it is freighters complaining about the wind.',
  ],
  sensors: [
    'I count everything that moves within forty kilometres. Birds, shuttles, weather. Mostly weather.',
    'The scopes never blink. Neither do I, on watch.',
  ],
  chief_engineer: [
    'The core has run nine years without a cough. She\'ll run nine more if people stop leaning on my rails.',
    'Heat, thrust, and lift — everything aboard is my problem eventually.',
  ],
  engineer: [
    'Coolant pressure, conduit patches, and whatever the chief says is next. The hum tells you everything, if you listen.',
    'I keep the lower decks warm and the lift field cold. Don\'t touch the orange valves.',
  ],
  quartermaster: [
    'Everything that comes up the tether crosses my ledger. {CITY} eats, and we weigh what it eats.',
    'Crates in, crates out, and not one gram unaccounted. Ask the ground quartermasters how rare that is.',
  ],
  cargohand: [
    'Lift, lash, stack, repeat. The hold never empties for long over a city this size.',
    'The work is heavy and the view is better than any dockside in the world.',
  ],
  cook: [
    'Three dozen mouths, four watches, one galley. I feed the whole sky, near enough.',
    'The captain gets the same stew as the deckhands. That is ship law, and it is MY law.',
  ],
  medic: [
    'Burns from the engine deck, bruises from the cargo bay, altitude headaches from new hands. Quiet ship, mostly.',
    'I patch the crew and I mind the air. At this height you respect the air.',
  ],
  deckhand: [
    'I walk the foredeck rail at dawn and there is no better post in either civilization.',
    'Scrub, splice, signal, repeat. When the fog rolls under the keel you are standing on the sky.',
  ],
  steward: [
    'I keep the lounge, the linens, and the peace. A tired crew is a clumsy crew.',
    'Small comforts hold a ship together as surely as rivets do.',
  ],
};

/** "Thirty-six" — the roster size in words for the crew topic. */
function crewCountWord(n: number): string {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty'];
  if (n < 20) return ones[n];
  return n % 10 === 0 ? tens[Math.floor(n / 10)] : `${tens[Math.floor(n / 10)]}-${ones[n % 10].toLowerCase()}`;
}

/** The shared topics every crew member can speak to (crew-line variants by
 *  role group where it matters). {SHIP}/{CITY}/{CIV} substituted. */
export function crewTopics(seed: string, site: SettlementSite, member: CrewMember): CrewTopic[] {
  const civ = civilizationOf(site.siteSeed);
  const ship = skyShipName(seed, site);
  const city = cityName(seed, site);
  const civName = civ === 'a' ? 'the Aurelian Concord' : 'the Borean Compact';
  const v = (a: string, b: string): string => (civ === 'a' ? a : b);
  const sub = (t: string): string => t
    .replaceAll('{SHIP}', ship).replaceAll('{CITY}', city).replaceAll('{CIV}', civName);
  const bridgeRole = member.deck === 'bridge';
  const engRole = member.deck === 'engineering' || member.deck === 'cargo';
  const topics: CrewTopic[] = [
    {
      key: 'ship', label: 'the ship',
      text: sub(v(
        'The {SHIP}. Concord-built, five decks from keel to bridge, and she has held station over {CITY} since before I signed aboard. The grounders call her "the roof of the city."',
        'The {SHIP}. Compact iron, five decks, and she rides the cold air over {CITY} like a gull that decided to stay. We keep her up; she keeps us honest.',
      )),
    },
    {
      key: 'city_below', label: 'the city below',
      text: sub(v(
        'That is {CITY} under the keel — gold roofs, the processional way, the whole Concord bustle of it. From the foredeck you can watch the market crowds move like weather.',
        '{CITY}, spread out below the hull. On a clear watch you can see the walls, the fields, the burial ground on the quiet edge. A city looks honest from up here.',
      )),
    },
    {
      key: 'decks', label: 'the decks',
      text: sub('You came in through the concourse — the hangar hall aft, where the cargo lives. Forward of that the stack: engineering at the keel, the mess and berthing, officer country and the medbay, the bridge on top. Then the spine gallery runs three hundred and fifty metres to the prow deck — the longest walk in the sky. Mind the stair rails.'),
    },
    {
      key: 'foredeck', label: 'the foredeck',
      text: sub(v(
        'The prow deck runs two hundred and sixty metres, and the last sixty hang past the nose over nothing — parades have been held on smaller ground. When the sun drops behind {CITY}\'s towers, half the watch finds a reason to be out there.',
        'Two hundred and sixty metres of open deck, the tip out past the hull over a kilometre of air. First-timers hold the rail. Old hands eat their supper out there in a crosswind.',
      )),
    },
    {
      key: 'engines', label: 'the engines',
      text: sub(engRole
        ? 'Lift core amidships, thrusters aft, and the hum in the plating that tells you all is well. When the hum changes, THEN you worry.'
        : 'Ask the engineers, and stand clear of the orange valves when you do. All I know is the deck hums and the ship stays up, and I am grateful for both.'),
    },
    {
      key: 'captain', label: 'the captain',
      text: sub(member.role === 'captain'
        ? 'You are speaking to her master. Say what you came to say.'
        : v(
          'The captain has held the conn longer than most mayors below have held office. Fair, exact, and fonder of {CITY} than she lets on.',
          'The captain does not waste words, and the ship does not waste altitude. The Compact way. We would follow the conn into a whiteout.',
        )),
    },
    {
      key: 'crew', label: 'the crew',
      text: sub(`${crewCountWord(ROSTER.length)} hands and the watch rotation. Bridge crew, engine gang, the hold, the galley, the medbay — everyone eats at the same mess and everyone stands a foredeck watch. You get to know people, a kilometre up.`),
    },
    {
      key: 'work', label: 'your work',
      text: sub(WORK_LINES[member.role][civ === 'a' ? 0 : 1]),
    },
    {
      key: 'shore', label: 'shore leave',
      text: sub(v(
        'The tether-jump down to {CITY} takes a breath and a half. Market day below is worth the queue at the boarding hall — just be back before the watch bell.',
        'We go down when the rotation allows. A tavern hearth, the bazaar, solid ground underfoot — then the queue at the boarding hall and glad to be home aloft.',
      )),
    },
  ];
  if (bridgeRole) {
    topics.push({
      key: 'skies', label: 'the skies',
      text: sub(v(
        'Freighters on the trade lanes, shuttles on the pads, and weather off the high ground. When war drums sound below, we log every wing that rises. The sky forgets nothing.',
        'Wind first, traffic second, everything else third. The sky over {CITY} is a working road, and we are its tollhouse — though we charge only patience.',
      )),
    });
  }
  return topics;
}

/** A short greeting line for the talk window header. */
export function crewGreeting(seed: string, site: SettlementSite, member: CrewMember): string {
  const ship = skyShipName(seed, site);
  const civ = civilizationOf(site.siteSeed);
  const v = (a: string, b: string): string => (civ === 'a' ? a : b);
  if (member.role === 'captain') {
    return v(`Welcome aboard the ${ship}. Few grounders make the climb — make it count.`,
      `You stand on the ${ship}. State your business, and mind the deck lines.`);
  }
  if (member.deck === 'foredeck') {
    return v('Careful by the rail — the first look down takes everyone the same way.',
      'Hold the rail if you need to. Everyone does, the first time.');
  }
  return v(`Visitor on deck! Welcome to the ${ship}.`, `A grounder aboard? Welcome to the ${ship}, then.`);
}
